import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useParams, Navigate } from 'react-router-dom';
import SupportForm from './components/SupportForm';
import HomeScreen from './screens/home/HomeScreen';
import PricingScreen from './screens/pricing/PricingScreen';
import ContactScreen from './screens/contact/ContactScreen';
import SiteNav from './components/shared/SiteNav';
import SiteFooter from './components/shared/SiteFooter';
import homeHero from './assets/DDHAPPS/white-top-home.webp';
import pricingHero from './assets/DDHAPPS/white-top-price.webp';
import contactHero from './assets/DDHAPPS/white-top-talking.webp';
import rm1 from './assets/RiddleMe/1.webp';
import rm2 from './assets/RiddleMe/2.webp';
import rm3 from './assets/RiddleMe/3.webp';
import rm4 from './assets/RiddleMe/4.webp';
import rm5 from './assets/RiddleMe/5.webp';
import rm6 from './assets/RiddleMe/6.webp';
import rm7 from './assets/RiddleMe/7.webp';
import RiddleMePrivacyPolicy from './components/RiddleMePrivacyPolicy';
import AppLinkLanding from './components/AppLinkLanding';
import RiddleSubmitForm from './components/RiddleSubmitForm';
import AccountScreen from './screens/account/AccountScreen';
import AdminScreen from './screens/admin/AdminScreen';
import ProtectedRoute from './components/ProtectedRoute';
import LoginModal from './components/LoginModal';
import ErrorBoundary from './components/shared/ErrorBoundary';
import PrivacyPolicy from './components/PrivacyPolicy';
import { onAuthStateChanged } from 'firebase/auth';
import { AuthProvider } from './context/AuthContext';
import { LoginModalProvider } from './context/LoginModalContext';
import { WelcomeOfferProvider, useWelcomeOffer } from './context/WelcomeOfferContext';
import { recordVisit, getTrackingLink } from './utils/trackingLinks';
import { isDdhappsAccount } from './utils/auth';
import { auth } from './firebaseDDH';

const DEFAULT_TAB_TITLE = typeof document !== 'undefined' ? document.title : '';
const RIDDLEME_TAB_TITLE = 'RiddleMe - The Daily Riddle';
const RIDDLEME_FAVICON = '/assets-in-site/icon.png';
// Prefer a real PNG favicon — Safari is unreliable with PNG-masquerading-as-.ico
const DEFAULT_FAVICON = '/favicon-48.png';
const MAIN_ROUTES = ['/', '/pricing', '/contact'];
// Routes that get the site nav and footer. The signed-in areas are included so
// the privacy policy is reachable from where we process people's data, and the
// policy itself so there's a way back to the site from it. The RiddleMe pages
// are deliberately left bare — they belong to the app, not this site.
const CHROME_ROUTES = [...MAIN_ROUTES, '/account', '/admin', '/privacypolicy'];

function setFavicon(href) {
  let iconLink = document.querySelector("link[rel='icon']");
  if (!iconLink) {
    iconLink = document.createElement('link');
    iconLink.setAttribute('rel', 'icon');
    document.head.appendChild(iconLink);
  }
  iconLink.setAttribute('href', href);
}

function TrackingLanding() {
  const { token } = useParams();
  const { setOffer } = useWelcomeOffer();

  useEffect(() => {
    // This component unmounts almost immediately via <Navigate> below, so we
    // can't wait on a React-state-driven auth check (the effect would never
    // get a chance to re-run once it resolved) or trust `auth.currentUser`
    // synchronously (Firebase hasn't finished checking for a persisted
    // session yet on a fresh tab, so it reads as null even for a logged-in
    // admin). Subscribing directly to Firebase's own listener isn't tied to
    // this component's lifecycle, so it still fires — with the real,
    // resolved auth state — after the redirect has already happened.
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      unsubscribe();
      if (!isDdhappsAccount(firebaseUser)) {
        recordVisit(token).catch(() => {});
      }
    });
  }, [token]);

  useEffect(() => {
    getTrackingLink(token).then((link) => {
      if (!link) return;
      setOffer({
        name: link.name,
        category: link.category,
        offerType: link.offerType,
        discountPercent: link.discountPercent,
        customPrice: link.customPrice,
        customDescription: link.customDescription,
        essentialPlanMode: link.essentialPlanMode,
        essentialPlanPercent: link.essentialPlanPercent,
        essentialPlanCustom: link.essentialPlanCustom,
        activePlanMode: link.activePlanMode,
        activePlanPercent: link.activePlanPercent,
        activePlanCustom: link.activePlanCustom,
      });
    }).catch(() => {});
  }, [token, setOffer]);

  return <Navigate to="/" replace />;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  const mounted = React.useRef(false);
  useEffect(() => {
    if (!mounted.current) { mounted.current = true; return; }
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppShell() {
  const { pathname } = useLocation();
  const showChrome = CHROME_ROUTES.includes(pathname);
  const isRiddleMeRoute = pathname.toLowerCase().startsWith('/riddleme');

  useEffect(() => {
    if (isRiddleMeRoute) {
      document.title = RIDDLEME_TAB_TITLE;
      setFavicon(RIDDLEME_FAVICON);
    } else {
      document.title = DEFAULT_TAB_TITLE;
      setFavicon(DEFAULT_FAVICON);
    }
  }, [isRiddleMeRoute]);

  useEffect(() => {
    [homeHero, pricingHero, contactHero, rm1, rm2, rm3, rm4, rm5, rm6, rm7].forEach(src => { new Image().src = src; });
  }, []);

  return (
    <>
      <ScrollToTop />
      {showChrome && <SiteNav />}
      <div>
        <Routes>
          <Route path="/" element={<HomeScreen />} />
          <Route path="/pricing" element={<PricingScreen />} />
          <Route path="/contact" element={<ContactScreen />} />
          <Route path="/support" element={<SupportForm />} />
          <Route path="/privacypolicy" element={<PrivacyPolicy />} />
          <Route path="/riddleme/privacypolicy" element={<RiddleMePrivacyPolicy />} />
          <Route path="/riddleme/submit-riddle" element={<RiddleSubmitForm />} />
          <Route path="/riddleme/app" element={<AppLinkLanding />} />
          <Route path="/open" element={<AppLinkLanding />} />
          <Route path="/account" element={<ProtectedRoute><AccountScreen /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute requireDdhapps><AdminScreen /></ProtectedRoute>} />
          <Route path="/:token" element={<TrackingLanding />} />
        </Routes>
      </div>
      {showChrome && <SiteFooter />}
      <LoginModal />
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
      <LoginModalProvider>
        <WelcomeOfferProvider>
          {/* Opting in early to two v6 → v7 behaviour changes. Both are the
              default in v7 anyway, and neither affects this app — we use no
              splat routes and no Suspense boundaries. Purely so the console
              stays clear of warnings. */}
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <AppShell />
          </Router>
        </WelcomeOfferProvider>
      </LoginModalProvider>
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
